import { ProjectMemberRole } from "@prisma/client";
import { MouseEvent, useRef, useState } from "react";
import Badge from "@/app/(components)/generics/Badge/Badge";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectMemberData } from "@/app/types/project/ProjectMember";
import { UProjectMember } from "@/app/utils/UProjectMember";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import styles from "./MemberSelector.module.css";

type Props = {
  value: number | null;
  members: ProjectMemberData[];
  onChange: (memberId: number | null) => void;
  onAddClick: () => void;
  params: PageParams;
  currentUserId?: number | null;
  onAssignMe?: () => void;
};

export default function MemberSelector(props: Props) {
  const T = useT();
  const { value, members, onChange, onAddClick, params, currentUserId, onAssignMe } = props;

  const projectId = ProjectStore((state) => state.currentProject?.id);
  const canManage = UProjectMember.canManageMembers(UProjectMember.getRole(members, currentUserId));
  const atLimit = members.length >= Limits.MAX_PROJECT_MEMBERS;

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = members.find((m) => m.id === value) ?? null;
  const currentUserMember = currentUserId
    ? members.find((m) => m.userId === currentUserId)
    : undefined;
  const otherMembers = currentUserMember
    ? members.filter((m) => m.id !== currentUserMember.id)
    : members;
  const isCurrentUser = selected && currentUserMember && selected.id === currentUserMember.id;

  const handleSelect = (memberId: number | null) => {
    onChange(memberId);
    setIsOpen(false);
  };

  const handleAdd = () => {
    setIsOpen(false);
    onAddClick();
  };

  const handleEdit = (e: MouseEvent, member: ProjectMemberData) => {
    e.stopPropagation();
    if (!projectId) {
      return;
    }
    setIsOpen(false);
    ModalStore.getState().openProjectMemberForm(params, projectId, { member });
  };

  const handleDelete = (e: MouseEvent, member: ProjectMemberData) => {
    e.stopPropagation();
    if (!projectId) {
      return;
    }
    setIsOpen(false);
    ModalStore.getState().openDeleteProjectMember(params, projectId, member, (deleted) => {
      if (value === deleted.id) {
        onChange(null);
      }
    });
  };

  const renderActions = (member: ProjectMemberData) => {
    if (!canManage) {
      return null;
    }
    return (
      <div className={styles.itemActions}>
        <Button
          isLink={false}
          type={ButtonType.GHOST}
          iconOnly={true}
          min={true}
          className={styles.actionBtn}
          beforeIcon={<IconEdit width={15} height={15} />}
          onClick={(e: MouseEvent) => handleEdit(e, member)}
          style={{ color: "var(--color-grey-6)" }}
        />
        {member.role !== ProjectMemberRole.OWNER && (
          <Button
            isLink={false}
            type={ButtonType.GHOST}
            iconOnly={true}
            min={true}
            className={styles.actionBtn}
            beforeIcon={<IconTrash size={15} />}
            onClick={(e: MouseEvent) => handleDelete(e, member)}
            style={{ color: "var(--color-red-1)" }}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Popover
        isVisible={isOpen}
        setIsVisible={setIsOpen}
        openAfterClick={true}
        closeWhenClickOutside={true}
        isPortaled={true}
      >
        <PopoverTrigger>
          <Badge className={UStyles.classNames(styles.trigger, !selected && styles.unassigned)}>
            <span className={styles.value}>
              {selected
                ? isCurrentUser
                  ? T(TKey.PROJECT_MEMBER_FILTER_ME)
                  : selected.name
                : T(TKey.PROJECT_MEMBER_UNASSIGNED)}
            </span>
            <div className={styles.arrowContainer}>
              {isOpen ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
            </div>
          </Badge>
        </PopoverTrigger>

        <PopoverContent position={{ default: { top: "8px", left: "0px" } }}>
          <div className={styles.popoverContent}>
            <div className={styles.list}>
              <div
                className={UStyles.classNames(styles.item, value === null && styles.active)}
                onClick={() => handleSelect(null)}
              >
                <span className={styles.itemLabel}>{T(TKey.PROJECT_MEMBER_UNASSIGNED)}</span>
              </div>
              {currentUserId && (
                <div
                  className={UStyles.classNames(
                    styles.item,
                    styles.itemMe,
                    currentUserMember ? value === currentUserMember.id && styles.active : false,
                  )}
                  onClick={() => {
                    if (currentUserMember) {
                      handleSelect(currentUserMember.id);
                    } else {
                      onAssignMe?.();
                      setIsOpen(false);
                    }
                  }}
                >
                  <span className={styles.itemLabel}>{T(TKey.PROJECT_MEMBER_FILTER_ME)}</span>
                  {currentUserMember && renderActions(currentUserMember)}
                </div>
              )}
              {otherMembers.map((member) => (
                <div
                  key={member.id}
                  className={UStyles.classNames(styles.item, value === member.id && styles.active)}
                  onClick={() => handleSelect(member.id)}
                >
                  <span className={styles.itemLabel}>{member.name}</span>
                  {renderActions(member)}
                </div>
              ))}
            </div>

            {canManage && !atLimit && (
              <div className={styles.footer}>
                <Button
                  min={true}
                  isLink={false}
                  full={true}
                  type={ButtonType.PRIMARY}
                  text={T(TKey.PROJECT_MEMBER_ADD) as string}
                  beforeIcon={<IconPlus size={14} />}
                  onClick={handleAdd}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
